import React, { useEffect, useState } from "react";
import { Megaphone, Flag, Mail, Plus, Trash2, Loader2, Edit3, Sparkles, Inbox, Check, X } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
    Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import api, { formatApiError } from "@/lib/api";
import { toast } from "@/components/ui/sonner";

const kindTone = (k) => ({
    info: "bg-sky-500/15 text-sky-700 dark:text-sky-400 border-sky-500/30",
    success: "bg-emerald-500/15 text-emerald-700 dark:text-emerald-400 border-emerald-500/30",
    warning: "bg-amber-500/15 text-amber-700 dark:text-amber-400 border-amber-500/30",
}[k] || "bg-slate-500/15 text-slate-600 border-slate-500/30");

const AnnouncementsTab = () => {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editing, setEditing] = useState(null); // null | { form, id? }

    const load = async () => {
        try { setLoading(true); const { data } = await api.get("/admin/announcements"); setRows(data); }
        catch (e) { toast.error(formatApiError(e)); }
        finally { setLoading(false); }
    };
    useEffect(() => { load(); }, []);

    const save = async () => {
        try {
            const body = { ...editing.form };
            if (editing.id) {
                await api.patch(`/admin/announcements/${editing.id}`, body);
            } else {
                await api.post("/admin/announcements", body);
            }
            toast.success("Saved.");
            setEditing(null); load();
        } catch (err) { toast.error(formatApiError(err)); }
    };

    const remove = async (id) => {
        if (!window.confirm("Delete announcement?")) return;
        try { await api.delete(`/admin/announcements/${id}`); toast.success("Deleted."); load(); }
        catch (err) { toast.error(formatApiError(err)); }
    };

    return (
        <div>
            <div className="mb-4 flex justify-between items-center">
                <div className="text-sm text-muted-foreground">Show a banner across the platform (or to a specific role).</div>
                <button onClick={() => setEditing({ form: { title: "", body: "", kind: "info", audience: "all", is_published: true, href: "" } })} className="btn-green text-xs inline-flex items-center gap-1" data-testid="ann-new">
                    <Plus size={12} /> New announcement
                </button>
            </div>
            {loading ? (
                <div className="py-10 flex justify-center"><Loader2 size={16} className="animate-spin text-emerald-500" /></div>
            ) : rows.length === 0 ? (
                <div className="card-soft p-8 text-center">
                    <Megaphone size={20} className="mx-auto text-muted-foreground" />
                    <div className="mt-2 text-sm text-muted-foreground">No announcements yet.</div>
                </div>
            ) : (
                <div className="space-y-2">
                    {rows.map((a) => (
                        <div key={a.id} className="card-soft p-4" data-testid={`ann-row-${a.id}`}>
                            <div className="flex items-start justify-between gap-3 flex-wrap">
                                <div className="min-w-0 flex-1">
                                    <div className="flex items-center gap-2 flex-wrap">
                                        <Badge className={`text-[10px] border ${kindTone(a.kind)}`}>{a.kind}</Badge>
                                        <Badge variant="outline" className="text-[10px] border-border">{a.audience}</Badge>
                                        {!a.is_published && <Badge variant="outline" className="text-[10px] border-border">draft</Badge>}
                                        <span className="font-heading font-semibold text-foreground">{a.title}</span>
                                    </div>
                                    <p className="mt-1 text-xs text-muted-foreground line-clamp-2">{a.body}</p>
                                </div>
                                <div className="flex items-center gap-2">
                                    <button onClick={() => setEditing({ id: a.id, form: { title: a.title, body: a.body, kind: a.kind, audience: a.audience, is_published: a.is_published, href: a.href || "" } })} className="btn-outline text-[11px] px-2 py-1 inline-flex items-center gap-1" data-testid={`ann-edit-${a.id}`}>
                                        <Edit3 size={11} /> Edit
                                    </button>
                                    <button onClick={() => remove(a.id)} className="text-[11px] px-2 py-1 rounded border border-destructive/30 text-destructive hover:bg-destructive/5 inline-flex items-center gap-1" data-testid={`ann-delete-${a.id}`}>
                                        <Trash2 size={11} /> Delete
                                    </button>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
                <DialogContent data-testid="ann-editor">
                    <DialogHeader>
                        <DialogTitle>{editing?.id ? "Edit announcement" : "New announcement"}</DialogTitle>
                        <DialogDescription>Announcements can target all users or a specific role.</DialogDescription>
                    </DialogHeader>
                    {editing && (
                        <div className="space-y-3">
                            <Input placeholder="Title *" value={editing.form.title} onChange={(e) => setEditing({ ...editing, form: { ...editing.form, title: e.target.value } })} data-testid="ann-title" />
                            <Textarea rows={3} placeholder="Body" value={editing.form.body} onChange={(e) => setEditing({ ...editing, form: { ...editing.form, body: e.target.value } })} data-testid="ann-body" />
                            <div className="grid grid-cols-2 gap-3">
                                <Select value={editing.form.kind} onValueChange={(v) => setEditing({ ...editing, form: { ...editing.form, kind: v } })}>
                                    <SelectTrigger data-testid="ann-kind"><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="info">Info</SelectItem>
                                        <SelectItem value="success">Success</SelectItem>
                                        <SelectItem value="warning">Warning</SelectItem>
                                    </SelectContent>
                                </Select>
                                <Select value={editing.form.audience} onValueChange={(v) => setEditing({ ...editing, form: { ...editing.form, audience: v } })}>
                                    <SelectTrigger data-testid="ann-audience"><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="all">All users</SelectItem>
                                        <SelectItem value="professional">Talent</SelectItem>
                                        <SelectItem value="employer">Employers</SelectItem>
                                        <SelectItem value="recruiter">Recruiters</SelectItem>
                                        <SelectItem value="hiring_team">Hiring team</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                            <Input placeholder="Link (optional, e.g. /pricing)" value={editing.form.href || ""} onChange={(e) => setEditing({ ...editing, form: { ...editing.form, href: e.target.value } })} data-testid="ann-href" />
                            <label className="flex items-center gap-2 text-sm cursor-pointer">
                                <input type="checkbox" checked={editing.form.is_published} onChange={(e) => setEditing({ ...editing, form: { ...editing.form, is_published: e.target.checked } })} className="accent-emerald-500" data-testid="ann-published" />
                                Published
                            </label>
                        </div>
                    )}
                    <DialogFooter>
                        <button onClick={() => setEditing(null)} className="btn-outline text-sm px-4 py-2">Cancel</button>
                        <button onClick={save} disabled={!editing?.form.title?.trim()} className="btn-green text-sm px-4 py-2 disabled:opacity-60" data-testid="ann-save">Save</button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
};

const FlagsTab = () => {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editing, setEditing] = useState(null);

    const load = async () => {
        try { setLoading(true); const { data } = await api.get("/admin/feature-flags"); setRows(data); }
        catch (e) { toast.error(formatApiError(e)); } finally { setLoading(false); }
    };
    useEffect(() => { load(); }, []);

    const save = async () => {
        try { await api.post("/admin/feature-flags", editing); toast.success("Saved."); setEditing(null); load(); }
        catch (err) { toast.error(formatApiError(err)); }
    };

    const toggle = async (flag) => {
        try { await api.post("/admin/feature-flags", { ...flag, enabled: !flag.enabled }); load(); }
        catch (err) { toast.error(formatApiError(err)); }
    };

    const remove = async (key) => {
        if (!window.confirm("Delete flag?")) return;
        try { await api.delete(`/admin/feature-flags/${key}`); load(); }
        catch (err) { toast.error(formatApiError(err)); }
    };

    return (
        <div>
            <div className="mb-4 flex justify-between items-center">
                <div className="text-sm text-muted-foreground">Simple boolean toggles read by the frontend to gate experimental features.</div>
                <button onClick={() => setEditing({ key: "", enabled: true, description: "" })} className="btn-green text-xs inline-flex items-center gap-1" data-testid="flag-new">
                    <Plus size={12} /> New flag
                </button>
            </div>
            {loading ? <div className="py-10 flex justify-center"><Loader2 size={16} className="animate-spin text-emerald-500" /></div>
                : rows.length === 0 ? (
                    <div className="card-soft p-8 text-center"><Flag size={20} className="mx-auto text-muted-foreground" /><div className="mt-2 text-sm text-muted-foreground">No flags yet.</div></div>
                ) : (
                    <div className="card-soft overflow-hidden">
                        <table className="w-full text-sm">
                            <thead><tr className="border-b border-border text-left text-[11px] uppercase text-muted-foreground">
                                <th className="p-3">Key</th><th className="p-3">Description</th><th className="p-3">Enabled</th><th className="p-3 text-right">Actions</th>
                            </tr></thead>
                            <tbody>
                                {rows.map((f) => (
                                    <tr key={f.key} className="border-b border-border/60 last:border-0" data-testid={`flag-row-${f.key}`}>
                                        <td className="p-3 font-mono text-xs">{f.key}</td>
                                        <td className="p-3 text-xs text-muted-foreground max-w-md">{f.description}</td>
                                        <td className="p-3">
                                            <button onClick={() => toggle(f)} className={`inline-block h-5 w-9 rounded-full transition-colors ${f.enabled ? "bg-emerald-500" : "bg-slate-300 dark:bg-slate-700"}`} data-testid={`flag-toggle-${f.key}`}>
                                                <span className={`block h-4 w-4 rounded-full bg-white transition-transform ${f.enabled ? "translate-x-4" : "translate-x-0.5"} mt-0.5`} />
                                            </button>
                                        </td>
                                        <td className="p-3 text-right">
                                            <button onClick={() => remove(f.key)} className="text-[11px] px-2 py-1 rounded border border-destructive/30 text-destructive hover:bg-destructive/5" data-testid={`flag-delete-${f.key}`}>Delete</button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
                <DialogContent data-testid="flag-editor">
                    <DialogHeader><DialogTitle>New feature flag</DialogTitle><DialogDescription>Snake-case key. Frontend reads it via /api/content/feature-flags.</DialogDescription></DialogHeader>
                    {editing && (
                        <div className="space-y-3">
                            <Input placeholder="Key (e.g. new_marketplace_ui)" value={editing.key} onChange={(e) => setEditing({ ...editing, key: e.target.value })} data-testid="flag-key" />
                            <Input placeholder="Description" value={editing.description} onChange={(e) => setEditing({ ...editing, description: e.target.value })} data-testid="flag-desc" />
                            <label className="flex items-center gap-2 text-sm cursor-pointer">
                                <input type="checkbox" checked={editing.enabled} onChange={(e) => setEditing({ ...editing, enabled: e.target.checked })} className="accent-emerald-500" data-testid="flag-enabled" />
                                Enabled
                            </label>
                        </div>
                    )}
                    <DialogFooter>
                        <button onClick={() => setEditing(null)} className="btn-outline text-sm px-4 py-2">Cancel</button>
                        <button onClick={save} disabled={!editing?.key?.trim()} className="btn-green text-sm px-4 py-2 disabled:opacity-60" data-testid="flag-save">Save</button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
};

const TemplatesTab = () => {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editing, setEditing] = useState(null);

    const load = async () => {
        try { setLoading(true); const { data } = await api.get("/admin/email-templates"); setRows(data); }
        catch (e) { toast.error(formatApiError(e)); } finally { setLoading(false); }
    };
    useEffect(() => { load(); }, []);

    const save = async () => {
        try { await api.post("/admin/email-templates", editing); toast.success("Saved."); setEditing(null); load(); }
        catch (err) { toast.error(formatApiError(err)); }
    };

    const remove = async (name) => {
        if (!window.confirm("Delete template?")) return;
        try { await api.delete(`/admin/email-templates/${name}`); load(); }
        catch (err) { toast.error(formatApiError(err)); }
    };

    return (
        <div>
            <div className="mb-4 flex justify-between items-center">
                <div className="text-sm text-muted-foreground">Store reusable email templates. Body supports {`{{variables}}`}.</div>
                <button onClick={() => setEditing({ name: "", subject: "", body: "", description: "" })} className="btn-green text-xs inline-flex items-center gap-1" data-testid="tpl-new">
                    <Plus size={12} /> New template
                </button>
            </div>
            {loading ? <div className="py-10 flex justify-center"><Loader2 size={16} className="animate-spin text-emerald-500" /></div>
                : rows.length === 0 ? (
                    <div className="card-soft p-8 text-center"><Mail size={20} className="mx-auto text-muted-foreground" /><div className="mt-2 text-sm text-muted-foreground">No templates yet.</div></div>
                ) : (
                    <div className="space-y-2">
                        {rows.map((t) => (
                            <div key={t.name} className="card-soft p-4" data-testid={`tpl-row-${t.name}`}>
                                <div className="flex items-start justify-between gap-3 flex-wrap">
                                    <div className="min-w-0 flex-1">
                                        <div className="font-mono text-xs text-emerald-600">{t.name}</div>
                                        <div className="font-heading font-semibold text-foreground mt-0.5">{t.subject}</div>
                                        {t.description && <div className="text-xs text-muted-foreground mt-0.5">{t.description}</div>}
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <button onClick={() => setEditing({ ...t })} className="btn-outline text-[11px] px-2 py-1 inline-flex items-center gap-1" data-testid={`tpl-edit-${t.name}`}>
                                            <Edit3 size={11} /> Edit
                                        </button>
                                        <button onClick={() => remove(t.name)} className="text-[11px] px-2 py-1 rounded border border-destructive/30 text-destructive hover:bg-destructive/5" data-testid={`tpl-delete-${t.name}`}>Delete</button>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
                <DialogContent data-testid="tpl-editor">
                    <DialogHeader><DialogTitle>Email template</DialogTitle><DialogDescription>Use {`{{variables}}`} that flows will substitute at send time.</DialogDescription></DialogHeader>
                    {editing && (
                        <div className="space-y-3">
                            <Input placeholder="Name (e.g. brief_approved)" value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} data-testid="tpl-name" />
                            <Input placeholder="Subject" value={editing.subject} onChange={(e) => setEditing({ ...editing, subject: e.target.value })} data-testid="tpl-subject" />
                            <Input placeholder="Description" value={editing.description} onChange={(e) => setEditing({ ...editing, description: e.target.value })} data-testid="tpl-desc" />
                            <Textarea rows={8} placeholder="Body (supports {{variables}})" value={editing.body} onChange={(e) => setEditing({ ...editing, body: e.target.value })} data-testid="tpl-body" />
                        </div>
                    )}
                    <DialogFooter>
                        <button onClick={() => setEditing(null)} className="btn-outline text-sm px-4 py-2">Cancel</button>
                        <button onClick={save} disabled={!editing?.name?.trim() || !editing?.subject?.trim()} className="btn-green text-sm px-4 py-2 disabled:opacity-60" data-testid="tpl-save">Save</button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default function ContentManagement() {
    return (
        <div data-testid="admin-content-management">
            <div className="mb-6">
                <p className="overline">Platform</p>
                <h1 data-testid="dash-page-title" className="mt-1 font-heading font-medium text-2xl sm:text-3xl tracking-tight text-foreground">
                    Content Management
                </h1>
                <p className="text-sm text-muted-foreground mt-1">Announcements · Feature flags · Email templates · Success stories.</p>
            </div>
            <Tabs defaultValue="announcements">
                <TabsList className="bg-card border border-border">
                    <TabsTrigger value="announcements" data-testid="cm-tab-announcements"><Megaphone size={13} className="mr-1.5" /> Announcements</TabsTrigger>
                    <TabsTrigger value="flags" data-testid="cm-tab-flags"><Flag size={13} className="mr-1.5" /> Feature flags</TabsTrigger>
                    <TabsTrigger value="templates" data-testid="cm-tab-templates"><Mail size={13} className="mr-1.5" /> Email templates</TabsTrigger>
                    <TabsTrigger value="stories" data-testid="cm-tab-stories"><Sparkles size={13} className="mr-1.5" /> Success stories</TabsTrigger>
                    <TabsTrigger value="submissions" data-testid="cm-tab-submissions"><Inbox size={13} className="mr-1.5" /> Story submissions</TabsTrigger>
                </TabsList>
                <TabsContent value="announcements" className="mt-6"><AnnouncementsTab /></TabsContent>
                <TabsContent value="flags" className="mt-6"><FlagsTab /></TabsContent>
                <TabsContent value="templates" className="mt-6"><TemplatesTab /></TabsContent>
                <TabsContent value="stories" className="mt-6"><SuccessStoriesTab /></TabsContent>
                <TabsContent value="submissions" className="mt-6"><StorySubmissionsTab /></TabsContent>
            </Tabs>
        </div>
    );
}

/* ----------------------------- Success Stories ---------------------------- */

const emptyStory = () => ({
    company_type: "",
    role_hired: "",
    time_to_shortlist: "",
    quote: "",
    author_name: "",
    results: [{ label: "", value: "" }, { label: "", value: "" }, { label: "", value: "" }],
    kind: "employer",
    is_published: true,
    order: 100,
});

const SuccessStoriesTab = () => {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editing, setEditing] = useState(null);

    const load = async () => {
        try {
            setLoading(true);
            const { data } = await api.get("/admin/success-stories");
            setRows(data || []);
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setLoading(false); }
    };
    useEffect(() => { load(); }, []);

    const save = async () => {
        try {
            const body = {
                ...editing.form,
                results: (editing.form.results || []).filter((r) => r.label?.trim() && r.value?.trim()),
            };
            if (editing.id) {
                await api.patch(`/admin/success-stories/${editing.id}`, body);
            } else {
                await api.post("/admin/success-stories", body);
            }
            toast.success("Story saved.");
            setEditing(null);
            load();
        } catch (err) { toast.error(formatApiError(err)); }
    };

    const remove = async (id) => {
        if (!window.confirm("Delete this success story? This cannot be undone.")) return;
        try {
            await api.delete(`/admin/success-stories/${id}`);
            toast.success("Story deleted.");
            load();
        } catch (err) { toast.error(formatApiError(err)); }
    };

    const setResultField = (idx, key, value) => {
        const results = [...(editing.form.results || [])];
        results[idx] = { ...(results[idx] || { label: "", value: "" }), [key]: value };
        setEditing({ ...editing, form: { ...editing.form, results } });
    };

    return (
        <div>
            <div className="mb-4 flex justify-between items-center flex-wrap gap-3">
                <div className="text-sm text-muted-foreground">
                    Case studies rendered on <span className="font-mono">/success-stories</span>. Placeholder stories show automatically until you publish at least one real story.
                </div>
                <button
                    onClick={() => setEditing({ form: emptyStory() })}
                    className="btn-green text-xs inline-flex items-center gap-1"
                    data-testid="story-new"
                >
                    <Plus size={12} /> New story
                </button>
            </div>
            {loading ? (
                <div className="py-10 flex justify-center"><Loader2 size={16} className="animate-spin text-emerald-500" /></div>
            ) : rows.length === 0 ? (
                <div className="card-soft p-8 text-center">
                    <Sparkles size={20} className="mx-auto text-muted-foreground" />
                    <div className="mt-2 text-sm text-muted-foreground">No success stories yet — the public page will show placeholder cards until you add one.</div>
                </div>
            ) : (
                <div className="space-y-2">
                    {rows.map((s) => (
                        <div key={s.id} className="card-soft p-4" data-testid={`story-row-${s.id}`}>
                            <div className="flex items-start justify-between gap-3 flex-wrap">
                                <div className="min-w-0 flex-1">
                                    <div className="flex items-center gap-2 flex-wrap">
                                        <Badge className="text-[10px] border border-emerald-500/30 bg-emerald-500/10 text-emerald-700">{s.kind}</Badge>
                                        {!s.is_published && <Badge variant="outline" className="text-[10px] border-border">draft</Badge>}
                                        <Badge variant="outline" className="text-[10px] border-border">order · {s.order}</Badge>
                                        <span className="font-heading font-semibold text-foreground">{s.role_hired || "Untitled role"}</span>
                                    </div>
                                    <p className="mt-1 text-xs text-muted-foreground line-clamp-2">
                                        {s.company_type ? `${s.company_type} — ` : ""}{s.quote}
                                    </p>
                                </div>
                                <div className="flex items-center gap-2">
                                    <button onClick={() => setEditing({ id: s.id, form: { ...s, results: (s.results?.length ? s.results : emptyStory().results) } })} className="btn-outline text-[11px] px-2 py-1 inline-flex items-center gap-1" data-testid={`story-edit-${s.id}`}>
                                        <Edit3 size={11} /> Edit
                                    </button>
                                    <button onClick={() => remove(s.id)} className="text-[11px] px-2 py-1 rounded border border-destructive/30 text-destructive hover:bg-destructive/5 inline-flex items-center gap-1" data-testid={`story-delete-${s.id}`}>
                                        <Trash2 size={11} /> Delete
                                    </button>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
                <DialogContent className="max-w-2xl" data-testid="story-editor">
                    <DialogHeader>
                        <DialogTitle>{editing?.id ? "Edit success story" : "New success story"}</DialogTitle>
                        <DialogDescription>Every field is optional except the quote — the public page skips missing fields gracefully.</DialogDescription>
                    </DialogHeader>
                    {editing && (
                        <div className="space-y-3">
                            <div className="grid grid-cols-2 gap-3">
                                <Input placeholder="Company type (e.g. Series C fintech · 220 employees)" value={editing.form.company_type} onChange={(e) => setEditing({ ...editing, form: { ...editing.form, company_type: e.target.value } })} data-testid="story-company-type" />
                                <Input placeholder="Role hired (e.g. Senior Product Designer)" value={editing.form.role_hired} onChange={(e) => setEditing({ ...editing, form: { ...editing.form, role_hired: e.target.value } })} data-testid="story-role" />
                            </div>
                            <div className="grid grid-cols-2 gap-3">
                                <Input placeholder="Time to shortlist (e.g. 3 days)" value={editing.form.time_to_shortlist} onChange={(e) => setEditing({ ...editing, form: { ...editing.form, time_to_shortlist: e.target.value } })} data-testid="story-tts" />
                                <Input placeholder="Author name (e.g. Head of People)" value={editing.form.author_name} onChange={(e) => setEditing({ ...editing, form: { ...editing.form, author_name: e.target.value } })} data-testid="story-author" />
                            </div>
                            <Textarea rows={4} placeholder="Customer quote" value={editing.form.quote} onChange={(e) => setEditing({ ...editing, form: { ...editing.form, quote: e.target.value } })} data-testid="story-quote" />
                            <div>
                                <div className="text-[11px] uppercase tracking-wider font-semibold text-muted-foreground mb-1.5">Results (up to 3 metric tiles)</div>
                                <div className="space-y-2">
                                    {(editing.form.results || []).slice(0, 3).map((r, idx) => (
                                        <div key={idx} className="grid grid-cols-5 gap-2">
                                            <Input placeholder={`Label ${idx + 1}`} value={r.label || ""} onChange={(e) => setResultField(idx, "label", e.target.value)} className="col-span-3" data-testid={`story-result-label-${idx}`} />
                                            <Input placeholder="Value" value={r.value || ""} onChange={(e) => setResultField(idx, "value", e.target.value)} className="col-span-2" data-testid={`story-result-value-${idx}`} />
                                        </div>
                                    ))}
                                </div>
                            </div>
                            <div className="grid grid-cols-3 gap-3 items-center">
                                <Select value={editing.form.kind} onValueChange={(v) => setEditing({ ...editing, form: { ...editing.form, kind: v } })}>
                                    <SelectTrigger data-testid="story-kind"><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="employer">Employer story</SelectItem>
                                        <SelectItem value="talent">Talent story</SelectItem>
                                    </SelectContent>
                                </Select>
                                <Input type="number" min={0} max={999} placeholder="Order" value={editing.form.order} onChange={(e) => setEditing({ ...editing, form: { ...editing.form, order: Number(e.target.value) || 0 } })} data-testid="story-order" />
                                <label className="flex items-center gap-2 text-sm cursor-pointer">
                                    <input type="checkbox" checked={editing.form.is_published} onChange={(e) => setEditing({ ...editing, form: { ...editing.form, is_published: e.target.checked } })} className="accent-emerald-500" data-testid="story-published" />
                                    Published
                                </label>
                            </div>
                        </div>
                    )}
                    <DialogFooter>
                        <button onClick={() => setEditing(null)} className="btn-outline text-sm px-4 py-2">Cancel</button>
                        <button onClick={save} disabled={!editing?.form?.quote?.trim()} className="btn-green text-sm px-4 py-2 disabled:opacity-60" data-testid="story-save">Save</button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
};


/* --------------------------- Story submissions --------------------------- */
const submissionStatusTone = (s) => ({
    pending: "bg-amber-500/15 text-amber-700 dark:text-amber-400 border-amber-500/30",
    approved: "bg-emerald-500/15 text-emerald-700 dark:text-emerald-400 border-emerald-500/30",
    rejected: "bg-red-500/15 text-red-700 dark:text-red-400 border-red-500/30",
}[s] || "bg-slate-500/15 text-slate-600 border-slate-500/30");

const StorySubmissionsTab = () => {
    const [rows, setRows] = useState([]);
    const [counts, setCounts] = useState({});
    const [filter, setFilter] = useState("pending");
    const [loading, setLoading] = useState(true);

    const load = async () => {
        try {
            setLoading(true);
            const params = filter === "all" ? {} : { status: filter };
            const { data } = await api.get("/admin/success-stories/submissions", { params });
            setRows(data.rows || []);
            setCounts(data.counts || {});
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setLoading(false); }
    };

    useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);

    const patch = async (id, next) => {
        try {
            await api.patch(`/admin/success-stories/submissions/${id}`, next);
            toast.success("Updated.");
            load();
        } catch (e) { toast.error(formatApiError(e)); }
    };

    return (
        <div>
            <div className="flex items-center justify-between flex-wrap gap-3">
                <div>
                    <h3 className="font-heading font-semibold text-xl text-foreground">Story submissions</h3>
                    <p className="text-sm text-muted-foreground mt-1">Public submissions from <span className="font-mono">/success-stories</span>. Approve to move them into the published cohort manually.</p>
                </div>
                <div className="flex flex-wrap gap-2" data-testid="submissions-filter">
                    {["pending", "approved", "rejected", "all"].map((s) => (
                        <button
                            key={s}
                            onClick={() => setFilter(s)}
                            data-testid={`submissions-filter-${s}`}
                            className={`inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs font-semibold transition-colors ${filter === s ? "bg-brand-navy text-white" : "bg-muted/40 text-foreground hover:bg-muted"}`}
                        >
                            {s.charAt(0).toUpperCase() + s.slice(1)}
                            {s !== "all" && (
                                <span className="rounded-full bg-black/10 dark:bg-white/10 px-1.5 py-0.5 text-[10px]">{counts[s] || 0}</span>
                            )}
                        </button>
                    ))}
                </div>
            </div>

            {loading ? (
                <div className="mt-6 py-8 flex justify-center"><Loader2 className="animate-spin text-emerald-500" /></div>
            ) : rows.length === 0 ? (
                <div className="mt-6 card-soft p-8 text-center text-sm text-muted-foreground" data-testid="submissions-empty">
                    No submissions in this bucket.
                </div>
            ) : (
                <div className="mt-6 space-y-3">
                    {rows.map((r) => (
                        <div key={r.id} className="card-soft p-5 space-y-3" data-testid={`submission-${r.id}`}>
                            <div className="flex items-start justify-between gap-3 flex-wrap">
                                <div>
                                    <div className="flex items-center gap-2 flex-wrap">
                                        <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-semibold ${submissionStatusTone(r.status)}`}>{r.status}</span>
                                        <span className="text-sm font-semibold text-foreground">{r.company_name}</span>
                                        <span className="text-xs text-muted-foreground">· {r.role_hired}</span>
                                    </div>
                                    <div className="mt-1 text-xs text-muted-foreground">
                                        {r.author_name}{r.author_title ? `, ${r.author_title}` : ""} · <a className="underline" href={`mailto:${r.author_email}`}>{r.author_email}</a>
                                        {r.time_to_shortlist ? ` · Shortlist ${r.time_to_shortlist}` : ""}
                                    </div>
                                </div>
                                <div className="flex gap-2">
                                    {r.status !== "approved" && (
                                        <button className="btn-outline text-xs px-3 py-1.5 inline-flex items-center gap-1" onClick={() => patch(r.id, { status: "approved" })} data-testid={`submission-approve-${r.id}`}>
                                            <Check size={12} /> Approve
                                        </button>
                                    )}
                                    {r.status !== "rejected" && (
                                        <button className="btn-outline text-xs px-3 py-1.5 inline-flex items-center gap-1" onClick={() => patch(r.id, { status: "rejected" })} data-testid={`submission-reject-${r.id}`}>
                                            <X size={12} /> Reject
                                        </button>
                                    )}
                                </div>
                            </div>
                            <blockquote className="rounded-lg border border-border bg-muted/30 p-3 text-sm text-foreground italic leading-relaxed">&ldquo;{r.quote}&rdquo;</blockquote>
                            {r.review_notes ? (
                                <div className="text-xs text-muted-foreground">Notes: {r.review_notes}</div>
                            ) : null}
                            <div className="text-[10px] text-muted-foreground">Submitted {new Date(r.created_at).toLocaleString()}</div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

